/**
 * Measurement table displayed beside a laboratory trend graph.
 */

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';
import type { LabTrendPoint } from '../../../types';
import { formatDisplayDate } from '../../../utils/displayDate';

interface LabTrendValueTableProps {
  points: LabTrendPoint[];
  unit: string;
  label: string;
  activeIndex?: number | null;
  onSelectIndex?: (index: number | null) => void;
}

/**
 * Render newest-first values for the currently plotted readings.
 *
 * The list is height-capped and scrolls internally. Because macOS hides overlay
 * scrollbars, the wrapper surfaces the reading count plus a "Scroll for more"
 * hint and a bottom fade cue whenever rows exist below the fold, so nurses can
 * tell the full list is available. Rows are clickable: selecting one activates
 * the matching point on the trend graph (tooltip, crosshair, dot). Indices are
 * local to `points`, matching the chart's active time window.
 *
 * @param props - Plotted readings plus the active point and selection callback.
 * @returns The trend measurement table.
 */
export function LabTrendValueTable({
  points,
  unit,
  label,
  activeIndex = null,
  onSelectIndex,
}: LabTrendValueTableProps) {
  const rows = [...points].reverse();
  const lastIndex = points.length - 1;
  const scrollRef = useRef<HTMLDivElement>(null);
  const [hasMoreBelow, setHasMoreBelow] = useState(false);

  /**
   * Recompute whether the list overflows its cap and is not yet at the bottom.
   *
   * @returns void
   */
  const updateScrollCue = (): void => {
    const element = scrollRef.current;
    if (!element) return;
    const remaining = element.scrollHeight - element.clientHeight - element.scrollTop;
    setHasMoreBelow(remaining > 4);
  };

  // Re-measure whenever the plotted readings (and therefore the row count) change.
  useLayoutEffect(updateScrollCue, [label, points.length]);

  // Re-measure on viewport resize, since the panel width drives row wrapping.
  useEffect(() => {
    window.addEventListener('resize', updateScrollCue);
    return () => window.removeEventListener('resize', updateScrollCue);
  }, []);

  /**
   * Map a newest-first row to its chronological history index and select it.
   *
   * @param displayIndex - Row position in the reversed (newest-first) list.
   * @returns void
   */
  const selectRow = (displayIndex: number): void => {
    onSelectIndex?.(lastIndex - displayIndex);
  };

  /**
   * Activate a row from the keyboard (Enter or Space).
   *
   * @param event - Keyboard event on the row.
   * @param displayIndex - Row position in the reversed list.
   * @returns void
   */
  const handleKeyDown = (event: KeyboardEvent<HTMLTableRowElement>, displayIndex: number): void => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      selectRow(displayIndex);
    }
  };

  return (
    <div className="trend-data-table-wrap">
      <div className="trend-data-table-caption">
        <span>
          {points.length} reading{points.length === 1 ? '' : 's'}
        </span>
        {hasMoreBelow && <span className="trend-data-scroll-hint">Scroll for more</span>}
      </div>
      <div className="trend-data-table-scroll" ref={scrollRef} onScroll={updateScrollCue}>
        <table className="trend-data-table" aria-label={`${label} measurements`}>
          <thead>
            <tr>
              <th>Date</th>
              <th className="trend-data-value-col">Value</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((point, index) => {
              const historyIndex = lastIndex - index;
              const isActive = activeIndex === historyIndex;
              const classes = [
                index === 0 ? 'trend-data-latest-row' : '',
                isActive ? 'trend-data-row--active' : '',
              ]
                .filter(Boolean)
                .join(' ');
              return (
                <tr
                  key={`${point.date}-${index}`}
                  className={classes || undefined}
                  role="button"
                  tabIndex={0}
                  aria-pressed={isActive}
                  aria-label={`Highlight ${formatDisplayDate(point.date)} reading on the trend graph`}
                  onClick={() => selectRow(index)}
                  onKeyDown={(event) => handleKeyDown(event, index)}
                >
                  <td>{formatDisplayDate(point.date)}</td>
                  <td className="trend-data-value-col">
                    {point.value}{' '}
                    <span className="trend-data-unit">{unit}</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div
        className={`trend-data-scroll-cue${hasMoreBelow ? ' trend-data-scroll-cue--visible' : ''}`}
        aria-hidden="true"
      />
    </div>
  );
}

export default LabTrendValueTable;
